import React, { useRef } from 'react';
import { action } from 'mobx';
import baseStyles from './register.module.css';
import registerMessages from './register_messages';
import { PublicPage } from 'ui/base/page/public_page/public_page';
import { BackButton } from 'ui/base/button/back_button';
import { LabeledInput, PasswordInput, TextInput } from 'ui/public/input';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ActiveButton } from 'ui/public/button';
import { RegisterStore } from './register_store';
import { CountryInput } from 'ui/standard/input/country_input/country_input';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { ButtonClickEvent, FormSubmitEvent } from 'ui/base/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { Modal } from 'ui/base/modal';
import { TermsOfService } from './terms_of_service/terms_of_service';

type RegisterStyles = {
  title: string;
  buttonLink: string;
  row: string;
  mobileNumber: string;
};

const themedStyles: ThemedStyles<RegisterStyles> = {
  [Theme.DAYLIGHT]: {
    title: baseStyles.daylightTitle,
    buttonLink: baseStyles.baseButtonLink,
    row: baseStyles.baseRow,
    mobileNumber: baseStyles.daylightMobileNumber,
  },
  [Theme.MIDNIGHT]: {
    title: baseStyles.midnightTitle,
    buttonLink: baseStyles.baseButtonLink,
    row: baseStyles.baseRow,
    mobileNumber: baseStyles.midnightMobileNumber,
  },
};

type BaseRegisterProps = {
  store: RegisterStore;
};

export const BaseRegister = observer(({ store }: BaseRegisterProps) => {
  const accounts = useAccountsDomain();
  const history = useHistory();
  const modalRef = useRef<HTMLDivElement>(null);

  const registerOrShowTerms = React.useCallback(action(async(event: FormSubmitEvent) => {
    event.preventDefault();
    await store.registerOrShowTerms(accounts, history, modalRef);
  }), [accounts, history, store, modalRef]);

  const acceptTermsThenRegister = React.useCallback(action(async(event: ButtonClickEvent) => {
    event.preventDefault();
    await store.acceptTermsThenRegister(accounts, history);
  }), [accounts, history, store]);

  const styles = useStyles(themedStyles);
  return (
    <PublicPage>
      <BackButton/>
      <div className={styles.title}>
        {registerMessages.register()}
      </div>
      <form onSubmit={registerOrShowTerms}>
        <div>
          <LabeledInput label={registerMessages.email()}>
            <TextInput store={store.email} id='email-address' name='email-address'/>
          </LabeledInput>
          <ErrorMessage message={store.emailError}/>
        </div>
        <div>
          <LabeledInput label={registerMessages.country()}>
            <CountryInput
              store={store.country}
              placeholder='Country'
            />
          </LabeledInput>
          <ErrorMessage message={store.countryError}/>
        </div>
        <div>
          <LabeledInput label={registerMessages.mobileNumber()}>
            <div className={styles.row}>
              <div className={styles.mobileNumber}>{store.country.prefix.value}</div>
              <TextInput store={store.mobileNumber} id='mobile' name='mobile'/>
            </div>
          </LabeledInput>
          <ErrorMessage message={store.mobileNumberError}/>
        </div>
        <div>
          <LabeledInput label={registerMessages.password()}>
            <PasswordInput store={store.password} id='password' name='password'/>
          </LabeledInput>
          <ErrorMessage message={store.passwordError}/>
        </div>
        <div className={styles.buttonLink}>
          <ActiveButton busy={store.busy} type='submit'>{registerMessages.next()}</ActiveButton>
        </div>
      </form>
      <Modal store={store.termsModal}>
        <TermsOfService modalRef={modalRef} onReject={store.termsModal.hideModal} onAccept={acceptTermsThenRegister}/>
      </Modal>
    </PublicPage>
  );
});

export const Register = () => {
  const [store] = React.useState(new RegisterStore());
  return <BaseRegister store={store}/>;
};
